function isLevelAllowed(label, level) {
  if (!label || level == null) return false;

  const normalizedLabel = label.toLowerCase();

  if (normalizedLabel === "all") return true;

  const levelMatch = String(level).match(/\d+/);

  const numericLevel = levelMatch ? Number(levelMatch[0]) : null;

  const isAlphabeticDesignation = numericLevel == null;

  if (
    typeof level === "string" &&
    level.toLowerCase().includes("accenture leadership")
  ) {
    return !normalizedLabel.includes("below");
  }

  if (normalizedLabel.includes("below")) {
    const maxLevel = Number(normalizedLabel.match(/\d+/)?.[0]);

    return numericLevel != null && numericLevel >= maxLevel;
  }

  if (normalizedLabel.includes("above")) {
    return isAlphabeticDesignation || numericLevel < 7;
  }

  return false;
}

function includesValue(str, value) {
  if (!str) return false;
  if (str.trim().toLowerCase() === "all") return true;
  if (!value) return false;

  return str
    .toLowerCase()
    .split(",")
    .map((v) => v.trim())
    .includes(value.toLowerCase());
}

export function isCourseEligible(course, userData, userLevel) {
  if (!course || !userData) return false;

  return (
    isLevelAllowed(course.level, userLevel) &&
    ((includesValue("Digital Customer", course.category) &&
      includesValue(course.sub_category, userData.capability)) ||
      (includesValue("Industry", course.category) &&
        includesValue(course.sub_category, userData.industry)) ||
      (includesValue("Technology", course.category) &&
        includesValue(course.sub_category, userData.category)) ||
      (includesValue(
        "GenAI for TDLC,Full-Stack GenAI,Specialization in Solutioning,AI for Solutioning,Agentic AI for Solutioning, Reinvention with Agentic AI",
        course.category,
      ) &&
        includesValue(course.capability, userData.capability) &&
        includesValue(course.industry, userData.industry) &&
        includesValue(course.role, userData.role)))
  );
}

const FOUNDATION_MANDATORY_RULES = [
  {
    level: "L7&8",
    role: "Design,Solution Arch",
    digital: 4,
    industry: 1,
    tech: 1,
  },
  {
    level: "L7&8",
    role: "Technology,Delivery",
    digital: 1,
    industry: 1,
    tech: 1,
  },
  { level: "L9-11", role: "Design", digital: 1, industry: 1, tech: 0 },
  { level: "L9-11", role: "All", digital: 1, industry: 1, tech: 1 },

  { level: "L6& above", role: "All", digital: "All", industry: 1, tech: 1 },
];

const FLAGSHIP_MANDATORY_RULES = [
  { level: "L6& above", role: "Design", digital: 1, industry: 1, tech: 0 },
  { level: "L6& above", role: "All", digital: 1, industry: 1, tech: 1 },

  { level: "L7&8", role: "Design", digital: 1, industry: 1, tech: 0 },
  { level: "L7&8", role: "All", digital: 1, industry: 1, tech: 1 },

  { level: "L9-11", role: "All", digital: 0, industry: 0, tech: 0 },
];

function matchFoundationRule(userLevel, userRole) {
  if (!userLevel) return null;

  const levelStr = String(userLevel).toLowerCase().trim();

  // Extract numeric level if present
  const numericMatch = levelStr.match(/\d+/);
  const numericLevel = numericMatch ? Number(numericMatch[0]) : null;

  // Any designation without numeric level
  // is considered senior leadership
  const isAlphabeticDesignation = numericLevel == null;

  const cleanRole = userRole?.toLowerCase().trim() || "";

  return FOUNDATION_MANDATORY_RULES.find((rule) => {
    const ruleLevel = rule.level.toLowerCase();

    let levelMatch = false;

    // Example: L6 & above
    if (ruleLevel.includes("& above")) {
      const min = Number(ruleLevel.match(/\d+/)?.[0]);

      levelMatch = isAlphabeticDesignation || numericLevel <= min;
    }

    // Example: L7&8
    else if (ruleLevel.includes("&")) {
      const levels = ruleLevel.match(/\d+/g)?.map(Number) || [];

      levelMatch = numericLevel != null && levels.includes(numericLevel);
    }

    // Example: L9-11
    else if (ruleLevel.includes("-")) {
      const [min, max] = ruleLevel.match(/\d+/g)?.map(Number) || [];

      levelMatch =
        numericLevel != null && numericLevel >= min && numericLevel <= max;
    }

    // Example: L6
    else {
      const exact = Number(ruleLevel.match(/\d+/)?.[0]);

      levelMatch = numericLevel != null && numericLevel === exact;
    }

    const roleMatch =
      rule.role === "All" ||
      rule.role
        .toLowerCase()
        .split(",")
        .map((r) => r.trim())
        .includes(cleanRole);

    return levelMatch && roleMatch;
  });
}

function matchFlagshipRule(userLevel, userRole) {
  if (!userLevel) return null;

  const levelStr = String(userLevel).toLowerCase().trim();

  // Extract numeric level if present
  const numericMatch = levelStr.match(/\d+/);
  const numericLevel = numericMatch ? Number(numericMatch[0]) : null;

  // Any designation without numeric level
  // is considered senior leadership
  const isAlphabeticDesignation = numericLevel == null;

  const cleanRole = userRole?.toLowerCase().trim() || "";

  return FLAGSHIP_MANDATORY_RULES.find((rule) => {
    const ruleLevel = rule.level.toLowerCase();

    let levelMatch = false;

    // Example: L6 & above
    if (ruleLevel.includes("& above")) {
      const min = Number(ruleLevel.match(/\d+/)?.[0]);

      levelMatch = isAlphabeticDesignation || numericLevel <= min;
    }

    // Example: L7&8
    else if (ruleLevel.includes("&")) {
      const levels = ruleLevel.match(/\d+/g)?.map(Number) || [];

      levelMatch = numericLevel != null && levels.includes(numericLevel);
    }

    // Example: L9-11
    else if (ruleLevel.includes("-")) {
      const [min, max] = ruleLevel.match(/\d+/g)?.map(Number) || [];

      levelMatch =
        numericLevel != null && numericLevel >= min && numericLevel <= max;
    }

    // Example: L6
    else {
      const exact = Number(ruleLevel.match(/\d+/)?.[0]);

      levelMatch = numericLevel != null && numericLevel === exact;
    }

    const roleMatch =
      rule.role === "All" ||
      rule.role
        .toLowerCase()
        .split(",")
        .map((r) => r.trim())
        .includes(cleanRole);

    return levelMatch && roleMatch;
  });
}

function groupByCategoryAndModule(courses) {
  const grouped = {};

  // Precompute module index map (O(n))
  const moduleIndexMap = new Map();

  courses.forEach((course, index) => {
    const category = course.category || "Uncategorized";
    const moduleName = course.modules || "General";

    if (!moduleIndexMap.has(moduleName)) {
      moduleIndexMap.set(moduleName, course.indexing ?? index);
    }

    if (!grouped[category]) {
      grouped[category] = {};
    }

    if (!grouped[category][moduleName]) {
      grouped[category][moduleName] = {
        Mandatory: [],
        Optional: [],
      };
    }

    grouped[category][moduleName][course.courseType].push(course);
  });

  Object.keys(grouped).forEach((category) => {
    const sortedModules = Object.keys(grouped[category])
      .sort((a, b) => {
        return (
          (moduleIndexMap.get(a) ?? Infinity) -
          (moduleIndexMap.get(b) ?? Infinity)
        );
      })
      .reduce((acc, moduleName) => {
        acc[moduleName] = grouped[category][moduleName];
        return acc;
      }, {});

    grouped[category] = sortedModules;
  });

  return grouped;
}

function applyFoundationCourseRules({ updatedCourses, userData, userLevel }) {
  function normalizeIndustry(industry) {
    const value = industry?.trim().toLowerCase();

    switch (value) {
      case "fs":
        return "financial service";

      case "resource":
        return "resources";

      default:
        return value;
    }
  }

  // Salesforce specific foundation rules
  if (
    (userData?.businessUnit?.trim().toLowerCase() || "song") === "salesforce"
  ) {
    const mandatoryIds = new Set();

    updatedCourses.forEach((course) => {
      if (course.modules?.trim().toLowerCase() !== "foundation") {
        return;
      }

      const category = course.category?.trim().toLowerCase();

      // 1. Digital Customer -> All mandatory
      if (category === "digital customer") {
        mandatoryIds.add(course.id);
      }

      // 2. Industry -> Only matching industry
      else if (category === "industry") {
        const userIndustry = normalizeIndustry(userData.industry);

        const matchesIndustry = course.industry
          ?.split(",")
          .some((i) => normalizeIndustry(i) === userIndustry);

        if (matchesIndustry) {
          mandatoryIds.add(course.id);
        }
      }

      // 3. Technology -> All except Salesforce
      else if (category === "technology") {
        const isSalesforceCourse = includesValue(
          course.sub_category,
          "Salesforce",
        );

        if (!isSalesforceCourse) {
          mandatoryIds.add(course.id);
        }
      }
    });

    return updatedCourses.map((c) => {
      const isFlagship = c.modules?.trim().toLowerCase() === "flagship";

      if (isFlagship) {
        return {
          ...c,
          courseType: "Optional",
        };
      }

      return {
        ...c,
        courseType: mandatoryIds.has(c.id) ? "Mandatory" : "Optional",
      };
    });
  }

  const foundationRule = matchFoundationRule(userLevel, userData.role);

  const mandatoryIds = new Set();

  const categoryToRuleKey = {
    "digital customer": "digital",
    industry: "industry",
    technology: "tech",
  };

  const processModuleCourses = (courses, rule) => {
    if (!rule || !courses.length) return;

    const categories = ["Digital Customer", "Industry", "Technology"];

    categories.forEach((category) => {
      const filtered = courses.filter(
        (c) => c.category?.trim().toLowerCase() === category.toLowerCase(),
      );

      if (!filtered.length) return;

      const key = categoryToRuleKey[category.toLowerCase()];

      const count = rule[key];

      if (!count || count === 0) return;

      if (count === "All") {
        filtered.forEach((c) => mandatoryIds.add(c.id));
        return;
      }

      if (count === 1 && userData) {
        let matched = null;

        if (category === "Digital Customer") {
          matched = filtered.find((c) =>
            includesValue(c.capability, userData.capability),
          );
        }

        if (category === "Industry") {
          matched = filtered.find((c) =>
            includesValue(c.industry, userData.industry),
          );
        }

        if (category === "Technology") {
          matched = filtered.find((c) =>
            includesValue(c.sub_category, userData.category),
          );
        }

        if (matched) {
          mandatoryIds.add(matched.id);
        } else {
          mandatoryIds.add(filtered[0].id);
        }

        return;
      }

      filtered.slice(0, count).forEach((c) => mandatoryIds.add(c.id));
    });
  };

  const foundationModuleCourses = updatedCourses.filter(
    (c) => c.modules?.trim().toLowerCase() === "foundation",
  );

  processModuleCourses(foundationModuleCourses, foundationRule);

  return updatedCourses.map((c) => {
    const isFlagship = c.modules?.trim().toLowerCase() === "flagship";

    if (isFlagship) {
      return {
        ...c,
        courseType: "Optional",
      };
    }

    return {
      ...c,
      courseType: mandatoryIds.has(c.id) ? "Mandatory" : "Optional",
    };
  });
}

function getProcessedCourses({
  coursesByHub,
  userData,
  userLevel,
  hubName = "Foundation",
}) {
  if (!coursesByHub || !userData) return [];

  const hubCourses =
    coursesByHub.find((hub) => hub.hubName === hubName)?.courses || [];

  let filteredHubCourses = hubCourses;

  if (hubName === "Foundation") {
    const userBusinessUnit =
      userData.businessUnit?.trim().toLowerCase() || "song";

    filteredHubCourses = hubCourses.filter((course) => {
      const courseBU = course.businessUnit?.trim().toLowerCase();

      if (userBusinessUnit === "salesforce") {
        return courseBU === "salesforce";
      }

      return courseBU === "song" || !course.businessUnit;
    });
  }

  let updatedCourses;

  if (hubName === "Foundation") {
    updatedCourses = filteredHubCourses.map((course) => ({
      ...course,
      courseType: "Optional",
    }));
  } else {
    updatedCourses = filteredHubCourses.map((course) => {
      const eligible = isCourseEligible(course, userData, userLevel);

      return {
        ...course,
        courseType:
          eligible && course.courseType === "Mandatory"
            ? "Mandatory"
            : "Optional",
      };
    });
  }

  if (hubName === "Foundation") {
    updatedCourses = applyFoundationCourseRules({
      updatedCourses,
      userData,
      userLevel,
    });
  }

  return updatedCourses;
}

export function getCoursesGroupedByCategoryAndModule({
  coursesByHub,
  userData,
  userLevel,
  hubName = "Foundation",
}) {
  const updatedCourses = getProcessedCourses({
    coursesByHub,
    userData,
    userLevel,
    hubName,
  });

  return groupByCategoryAndModule(updatedCourses);
}

export function getCoursesGroupedCourses({
  coursesByHub,
  userData,
  userLevel,
  hubName = "Foundation",
}) {
  return getProcessedCourses({
    coursesByHub,
    userData,
    userLevel,
    hubName,
  });
}

export function isFoundationUnlocked({ coursesByHub, userData, userLevel }) {
  if (!coursesByHub || !userData) return false;

  const grouped = getCoursesGroupedByCategoryAndModule({
    coursesByHub,
    userData,
    userLevel,
    hubName: "Foundation",
  });

  const mandatoryCourses = Object.values(grouped).flatMap((modules) =>
    Object.values(modules).flatMap((m) => m.Mandatory || []),
  );

  if (!mandatoryCourses.length) return true;

  return mandatoryCourses.every((course) => Boolean(course.completionDate));
}

export function applyScoreboardRules({ AIScoreboardCourses, userData }) {
  if (!AIScoreboardCourses || !userData) {
    return {
      scoreboardCoursesGroupedByModule: new Map(),
      totalPointsPerModule: new Map(),
    };
  }

  const industry = userData?.industry?.trim().toLowerCase();
  const capability = userData?.capability?.trim().toLowerCase();

  const moduleMap = AIScoreboardCourses.reduce((map, item) => {
    let processedItem = { ...item };

    if (item.category === "Foundation") {
      const courseName = (item.courseName || "").toLowerCase();

      let include = true;

      if (item.learning_modules === "Industry") {
        include = courseName.startsWith(industry);
      }

      if (item.learning_modules === "Digital Customer") {
        include =
          courseName.startsWith(capability) ||
          (capability === "ddp" &&
            courseName.startsWith("design & digital products"));
      }

      if (!include) {
        return map;
      }
    }

    if (item.category === "Foundation" && item.ignite_links) {
      const links = item.ignite_links.split("\n");
      let filteredLinks = [];

      if (item.learning_modules === "Industry" && userData?.industry) {
        filteredLinks = links.filter((link) =>
          link.trim().toLowerCase().startsWith(industry),
        );
      }

      if (
        item.learning_modules === "Digital Customer" &&
        userData?.capability
      ) {
        filteredLinks = links.filter(
          (link) =>
            link.trim().toLowerCase().startsWith(capability) ||
            (capability === "ddp" &&
              link
                .trim()
                .toLowerCase()
                .startsWith("design & digital products")),
        );
      }

      processedItem.ignite_links = filteredLinks.join("\n");
    }

    if (
      (item.learning_modules === "AI Foundations" ||
        item.learning_modules === "AI Systems Design") &&
      userData?.role
    ) {
      processedItem.ai_score =
        userData.role === "Technology" || userData.role === "Architect"
          ? 100
          : 200;
    } else {
      processedItem.ai_score = Number.parseInt(item.ai_score, 10);
      if (Number.isNaN(processedItem.ai_score)) {
        processedItem.ai_score = 0;
      }
    }

    if (!map.has(item.learning_modules)) {
      map.set(item.learning_modules, []);
    }

    map.get(item.learning_modules).push(processedItem);

    return map;
  }, new Map());

  const pointsMap = new Map();

  for (const [learningModule, courses] of moduleMap) {
    let flag = false;

    for (const course of courses) {
      if (course.courseName) {
        flag = true;
        break;
      }
    }

    // Each learningModule should have a total of 100 points
    flag && pointsMap.set(learningModule, 100);
  }

  return {
    scoreboardCoursesGroupedByModule: moduleMap,
    totalPointsPerModule: pointsMap,
  };
}
